/**
 * <recently-viewed> — Relewise-powered variant of recently-viewed.js, loaded
 * INSTEAD of it when the "Enable Relewise" theme setting is on (see
 * snippets/products-recently-viewed.liquid). Same element, same markup
 * contract, same designed UI ("Du har kigget på") — only the SOURCE of the
 * handle list changes: Relewise's server-side view history (cross-device for
 * logged-in klub members) instead of the first-party cookie.
 *
 * Rendering reuses the exact existing pipeline: each handle is fetched as
 * /products/<handle>?section_id=api-product-grid-item with the same animation
 * token replacement.
 *
 * LOADING STATE (Magnus 2026-09-21: "when reloading, the section is not shown
 * and then suddenly appears"). The section server-renders hidden — it cannot
 * know whether this visitor has any history — and used to pop in whole once
 * the consent user (up to 4 s), the recommendation request and a card fetch
 * per product had all resolved. The first-party cookie tells us the answer
 * synchronously, so a visitor who HAS history gets the row's space and the
 * shared skeleton immediately (see snippets/spejder-product-card-skeleton),
 * and only the cards themselves arrive late. A visitor with no history is
 * still shown nothing at all: no skeleton appears only to collapse. When Relewise has no history for this user (e.g. consent
 * declined → anonymous) or errors, it falls back to the original cookie flow
 * via Shopify.Products.showRecentlyViewed, so no visitor sees less than today.
 */
!(function () {
  'use strict';

  const selectors = { sectionId: '[data-section-id]' };

  if (customElements.get('recently-viewed')) return;

  customElements.define(
    'recently-viewed',
    class RelewiseRecentlyViewed extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest(selectors.sectionId);
        this.howManyToShow = parseInt(this.dataset.limit) || 3;
        this.target = this.dataset.target;
        this.wrapperId = this.dataset.wrapperId || this.id;
      }

      connectedCallback() {
        this.showSkeleton();
        this.loadFromRelewise();
      }

      get wrapper() {
        return document.querySelector(`#${this.wrapperId}`);
      }

      /* Handles the theme has recorded for this visitor, newest first — the
         same cookie the no-Relewise path reads (Shopify.Products). Its mere
         presence is what the skeleton is gated on; the handles Relewise
         answers with may differ. */
      cookieHandles() {
        const raw = (document.cookie.match(/(?:^|;\s*)shopify_recently_viewed=([^;]*)/) || [])[1];
        if (!raw) return [];
        return decodeURIComponent(raw).split(' ').filter(Boolean);
      }

      /* Placeholder cards in the row's own grid, held by the shared skeleton
         CSS ([data-relewise-hold], spejder.css "Recommendation rows"). Markup
         mirrors snippets/spejder-product-card-skeleton — this row is filled
         client-side from a cookie, so Liquid cannot render the shells. */
      showSkeleton() {
        const wrapper = this.wrapper;
        if (!wrapper) return;
        const count = Math.min(this.cookieHandles().length, this.howManyToShow);
        /* Below the section's own minimum it would not be shown even fully
           loaded, so there is nothing to hold space for. */
        if (count < (parseInt(this.dataset.minimum) || 1)) return;

        this.skeletonCount = count;
        wrapper.innerHTML = new Array(count).fill(
          '<div class="grid-item product-item spejder-card-skeleton" data-grid-item data-relewise-skeleton-card aria-hidden="true">' +
            '<div class="product-item__image"></div>' +
            '<div class="product-information"><div class="product-item__info"></div></div>' +
          '</div>'
        ).join('');
        wrapper.setAttribute('data-relewise-hold', '');
        wrapper.classList.remove('hidden');
        this.classList.remove('hidden');

        /* A recent-only section (the PDP's "Du har kigget på") renders its
           whole wrapper hidden — sections/related.liquid
           `recent_only_needs_default_hidden`. */
        const outer = this.closest('.related__wrapper');
        if (outer) outer.classList.remove('hidden');
      }

      /* The cookie path (Shopify.Products.moveAlong) fetches its cards ONE AT
         A TIME and appends each with `wrapper.innerHTML +=`, so clearing the
         shells when it starts left the row collapsed to nothing for a second
         before the first card landed — the very pop-in this is meant to
         remove. Instead each arriving card consumes one shell, so the row
         keeps its full width and fills left to right. Re-appending re-reads
         the wrapper's innerHTML, which is why moving the shells to the end
         sticks. */
      consumeSkeletonAsCardsArrive() {
        const wrapper = this.wrapper;
        if (!wrapper || !wrapper.querySelector('[data-relewise-skeleton-card]')) return;

        /* Every rearrangement below is itself a mutation, so the observer is
           re-entered on its own edits. The rule is therefore stated as a
           TARGET COUNT rather than "remove one now": re-running it after the
           same card changes nothing. (Stated as a delta it cascaded, and the
           first card drained every placeholder.) */
        const observer = new MutationObserver(() => {
          let shells = [...wrapper.querySelectorAll('[data-relewise-skeleton-card]')];
          if (!shells.length) return observer.disconnect();

          const cards = wrapper.querySelectorAll('.product-item:not(.spejder-card-skeleton)').length;
          if (!cards) return;

          /* Keep the placeholders behind the cards already rendered... */
          if (shells.some((el, i) => wrapper.children[wrapper.children.length - shells.length + i] !== el)) {
            shells.forEach((el) => wrapper.appendChild(el));
          }
          /* ...and hold the row at its original width: one placeholder stands
             in for every card still to come. */
          const target = Math.max(0, this.skeletonCount - cards);
          shells.slice(0, shells.length - target).forEach((el) => el.remove());

          if (!wrapper.querySelector('[data-relewise-skeleton-card]')) {
            observer.disconnect();
            wrapper.setAttribute('data-relewise-ready', '');
          }
        });
        observer.observe(wrapper, { childList: true });
        /* Whatever happens, nothing may outlive the load itself. */
        this.addEventListener('theme:recently-viewed:loaded', () => {
          observer.disconnect();
          this.settleSkeleton();
        }, { once: true });
      }

      /* Every outcome ends here: the hold is released so real cards show, and
         any shell still standing is dropped — nothing else replaces them. */
      settleSkeleton() {
        const wrapper = this.wrapper;
        if (!wrapper) return;
        wrapper.querySelectorAll('[data-relewise-skeleton-card]').forEach((el) => el.remove());
        wrapper.setAttribute('data-relewise-ready', '');
      }

      async loadFromRelewise() {
        const cfg = window.spejder && window.spejder.relewise;
        if (!cfg || !cfg.enabled || !window.Relewise) {
          this.loadFromCookie();
          return;
        }

        try {
          const handleKey = `${cfg.shop}_ShopifyHandle`;
          const user = await this.getUser();
          const request = new Relewise.RecentlyViewedProductsBuilder({
            language: cfg.language,
            currency: cfg.currency,
            /* The row also sits on the cart page (sections/cart.liquid), which
               names itself so Relewise's reports don't count it as the PDP. */
            displayedAtLocation: this.dataset.relewiseLocation || 'Product Details Page',
            user: user,
          })
            .setNumberOfRecommendations(this.howManyToShow)
            .setSelectedProductProperties({dataKeys: [handleKey]})
            .build();

          /* One batched request shared with the page's other rows ("Andre
             kunder købte også", placed recommendation sections) — see
             assets/relewise-batch.js. The single-request collection this used
             to build on its own is what the batcher now builds for everyone. */
          const result = window.spejder.relewiseBatch
            ? await window.spejder.relewiseBatch.recommend(request)
            : await new Relewise.Recommender(cfg.datasetId, cfg.apiKey, {serverUrl: cfg.serverUrl})
                .batchProductRecommendations(new Relewise.ProductsRecommendationCollectionBuilder().addRequest(request).build())
                .then((r) => r && r.responses && r.responses[0]);

          const recommendations = (result && result.recommendations) || [];
          const handles = recommendations.map((rec) => rec.data && rec.data[handleKey] && rec.data[handleKey].value).filter(Boolean);

          if (!handles.length) {
            this.loadFromCookie();
            return;
          }

          await this.renderHandles(handles.slice(0, this.howManyToShow));
        } catch (error) {
          this.loadFromCookie();
        }
      }

      getUser() {
        /* Shared helper (assets/relewise-batch.js) waits for the app embed's global;
           testing for it synchronously here always found nothing (2026-09-16). */
        if (window.spejder && window.spejder.relewiseUser) return window.spejder.relewiseUser();
        if (window.ShopifyRelewise && typeof window.ShopifyRelewise.getUserAsync === 'function') {
          return window.ShopifyRelewise.getUserAsync().catch(() => Relewise.UserFactory.anonymous());
        }
        return Promise.resolve(Relewise.UserFactory.anonymous());
      }

      /* Same per-handle section rendering as the original cookie flow. */
      async renderHandles(handles) {
        const wrapper = document.querySelector(`#${this.wrapperId}`);
        if (!wrapper) return;

        const anchor = wrapper.id ? `#${wrapper.id}` : '';
        const rendered = await Promise.all(
          handles.map((handle, index) =>
            fetch(`${window.theme.routes.root}products/${handle}?section_id=${this.target}`)
              .then((response) => (response.ok ? response.text() : ''))
              .then((markup) => {
                if (!markup) return '';
                let item = markup;
                if (item.includes('||itemAnimationDelay||')) item = item.replaceAll('||itemAnimationDelay||', index * 100);
                if (item.includes('||itemAnimationAnchor||')) item = item.replaceAll('||itemAnimationAnchor||', anchor);
                const holder = document.createElement('div');
                holder.innerHTML = item;
                const content = holder.querySelector('[data-api-content]');
                return content ? content.innerHTML : '';
              })
              .catch(() => '')
          )
        );

        /* A handle Relewise still holds but Shopify no longer resolves answers
           200 with an EMPTY card shell — no <img>, no product link, no title —
           so `response.ok` above cannot tell it from a real card and it rendered
           as a blank tile in the row (QA 2026-08-18). The product link is the
           discriminator the shell lacks. */
        const cards = rendered.filter((html) => html && html.includes('href="/products/'));
        if (!cards.length) {
          this.loadFromCookie();
          return;
        }

        wrapper.innerHTML = cards.join('');
        this.settleSkeleton();
        wrapper.classList.remove('hidden');
        this.dispatchEvent(new CustomEvent('theme:recently-viewed:loaded', {bubbles: false}));
      }

      /* Original behavior — first-party cookie via Shopify.Products (bundle).
         That library writes the markup itself, so blank shells can only be
         removed after it reports done — same rule as the Relewise path above,
         applied at the other end. */
      loadFromCookie() {
        if (!window.Shopify || !Shopify.Products) {
          this.settleSkeleton();
          return;
        }
        this.consumeSkeletonAsCardsArrive();
        Shopify.Products.showRecentlyViewed({
          howManyToShow: this.howManyToShow,
          wrapperId: this.wrapperId,
          section: this.section,
          target: this.target,
          onComplete: () => {
            this.dropBlankCards();
            this.dispatchEvent(new CustomEvent('theme:recently-viewed:loaded', {bubbles: false}));
          },
        });
      }

      /* Drop card elements with no product link, and keep the whole element
         hidden when nothing real is left — a heading over an empty row is the
         thing the cart page's :has() rule guards against. */
      dropBlankCards() {
        const wrapper = document.querySelector(`#${this.wrapperId}`);
        if (!wrapper) return;
        wrapper.querySelectorAll(':scope > *').forEach((card) => {
          if (!card.querySelector('a[href^="/products/"]')) card.remove();
        });
        if (!wrapper.children.length) {
          /* Nothing real to show after all: put the row back the way it was
             rendered, skeleton space included. */
          wrapper.classList.add('hidden');
          this.classList.add('hidden');
          const outer = this.closest('.related__wrapper');
          if (outer && !outer.querySelector('[data-related-section] [data-grid-item]')) outer.classList.add('hidden');
        }
      }
    }
  );
})();
